import { ANIMATION_KNOBS } from './knobs';
import { defaultPresetFor, presetsFor } from './presets';
import {
  ANIMATION_VARIANTS,
  DEFAULT_ANIMATION,
  DEFAULT_CONTROLS,
  DEFAULT_SOUND,
  isOpenerVariant,
  type AnimationConfig,
  type AnimationOptions,
  type AnimationVariant,
  type ControlsConfig,
  type SoundConfig,
} from './types';

type Plain = Record<string, unknown>;

const isPlain = (value: unknown): value is Plain =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isVariant = (value: unknown): value is AnimationVariant =>
  typeof value === 'string' && (ANIMATION_VARIANTS as readonly string[]).includes(value);

/** Where a knob writes a list of colours; a list anywhere else is nothing the library reads. */
const LIST_PATHS: ReadonlySet<string> = new Set(
  ANIMATION_KNOBS.filter((knob) => knob.kind === 'colorList').map((knob) => knob.path),
);

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'string');

/**
 * Keeps strings, finite numbers, booleans, nested plain objects and — where a
 * knob writes one — a list of strings; drops the rest.
 */
function sanitizeOptions(raw: unknown, prefix = ''): Plain | undefined {
  if (!isPlain(raw)) return undefined;
  const out: Plain = {};
  for (const [key, value] of Object.entries(raw)) {
    const path = prefix + key;
    if (isPlain(value)) {
      const nested = sanitizeOptions(value, `${path}.`);
      if (nested) out[key] = nested;
    } else if (
      typeof value === 'string' ||
      typeof value === 'boolean' ||
      (typeof value === 'number' && Number.isFinite(value)) ||
      (LIST_PATHS.has(path) && isStringList(value))
    ) {
      out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Normalises whatever `design_system.animation` holds into an AnimationConfig.
 * Anything unrecognisable — missing section, unknown variant, garbage shape —
 * falls back to the slot lane, so a bad row can never break the reveal.
 */
export function parseAnimation(raw: unknown): AnimationConfig {
  if (!isPlain(raw)) return DEFAULT_ANIMATION;
  const variantRaw = raw['variant'];
  if (!isVariant(variantRaw)) return DEFAULT_ANIMATION;
  if (!isOpenerVariant(variantRaw)) return parseSlot(raw['options']);

  const presetRaw = raw['preset'];
  const preset =
    typeof presetRaw === 'string' && presetsFor(variantRaw).includes(presetRaw)
      ? presetRaw
      : defaultPresetFor(variantRaw);
  const options = sanitizeOptions(raw['options']);
  const sound = parseSound(raw['sound']);
  const controls = parseControls(raw['controls']);

  return {
    variant: variantRaw,
    preset,
    ...(options ? { options: options as AnimationOptions } : {}),
    ...(sound ? { sound } : {}),
    ...(controls ? { controls } : {}),
  };
}

/** The slot lane has no preset, sound or controls; of its options only `slot` is its own. */
function parseSlot(rawOptions: unknown): AnimationConfig {
  const slot = isPlain(rawOptions) ? sanitizeOptions(rawOptions['slot']) : undefined;
  return slot ? { variant: 'slot', options: { slot } as AnimationOptions } : { variant: 'slot' };
}

/** A sound section with a bad shape reads as the default rather than as silence. */
function parseSound(raw: unknown): SoundConfig | undefined {
  if (!isPlain(raw)) return undefined;
  const enabled = typeof raw['enabled'] === 'boolean' ? raw['enabled'] : DEFAULT_SOUND.enabled;
  const volumeRaw = raw['volume'];
  const volume =
    typeof volumeRaw === 'number' && Number.isFinite(volumeRaw)
      ? Math.min(1, Math.max(0, volumeRaw))
      : DEFAULT_SOUND.volume;
  return { enabled, volume };
}

/** A controls section with a bad shape keeps the button — never strands a
 *  player without a way to open the pack. */
function parseControls(raw: unknown): ControlsConfig | undefined {
  if (!isPlain(raw)) return undefined;
  const ripButton = typeof raw['ripButton'] === 'boolean' ? raw['ripButton'] : DEFAULT_CONTROLS.ripButton;
  const rarityBackdrop = raw['rarityBackdrop'];
  return typeof rarityBackdrop === 'boolean' ? { ripButton, rarityBackdrop } : { ripButton };
}
